"use client";

import { useEffect, useRef, useState } from "react";
import type { CaseData, CtaComponentProps } from "@/cms/models";
import PropertyGalleryCard from "@/components/PropertyGalleryCard";
import PropertyRowCard from "@/components/PropertyRowCard";
import PropertyEditorialCard from "@/components/PropertyEditorialCard";
import RippleCtaButtonComponent from "@/components/ui/RippleCtaButtonComponent";
import { loadPropertySelectionPage } from "@/actions/property-list";
import CardMotion from "../CardMotion";
import { useLookSystem } from "@/components/motion/LookMotion";

/** Ejendomme's »Se flere«: the server's first cases, then `count` more per click, paged by Core's offset (`next`) so
 *  a case dropped after the query neither repeats nor leaves a gap. The card is named, since a render function cannot
 *  cross from the server. Outside an interactive page the button is drawn but does nothing. */
export default function PropertyMore({
  initial,
  total,
  next,
  skip,
  card,
  layout,
  button,
  flyIn = false,
  interactive = true,
  request,
}: {
  initial: CaseData[];
  total: number;
  next: number;
  skip: number;
  card: "editorial" | "gallery" | "row";
  layout: "grid" | "list";
  button: { className: string; config: CtaComponentProps };
  flyIn?: boolean;
  interactive?: boolean;
  request: { sectionKey: string; tab: number | null };
}) {
  const lookSystem = useLookSystem();
  const [items, setItems] = useState(initial);
  const [offset, setOffset] = useState(next);
  const [all, setAll] = useState(total);
  const [busy, setBusy] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const focusIndex = useRef<number | null>(null);
  useEffect(() => {
    const index = focusIndex.current;
    if (index === null) return;
    focusIndex.current = null;
    list.current
      ?.querySelector<HTMLElement>(`[data-card-index="${index}"] a`)
      ?.focus();
  }, [items]);
  const more = async () => {
    if (busy || !interactive) return;
    setBusy(true);
    const page = await loadPropertySelectionPage({
      path: window.location.pathname,
      sectionKey: request.sectionKey,
      tab: request.tab,
      offset,
    }).catch(() => null);
    setBusy(false);
    if (!page || page.error) return;
    const shown = new Set(items.map((item) => item.id));
    const added = page.items.filter((item) => !shown.has(item.id));
    focusIndex.current = added.length ? items.length : null;
    setItems([...items, ...added]);
    setOffset(page.next > offset ? page.next : all - skip);
    setAll(page.total);
  };
  return (
    <>
      <div ref={list} className={layout === "list" ? "pf-list" : "pf-grid"}>
        {items.map((property, index) => (
          <CardMotion
            flyIn={flyIn}
            index={index}
            className="pf-items"
            key={property.id}
            data-card-index={index}
          >
            {card === "row" ? (
              <PropertyRowCard
                property={property}
                shadow="none"
                cardPhoto={lookSystem}
              />
            ) : card === "gallery" ? (
              <PropertyGalleryCard
                property={property}
                shadow="none"
                cardPhoto={lookSystem}
              />
            ) : (
              <PropertyEditorialCard
                property={property}
                layout="grid"
                cardPhoto={lookSystem}
              />
            )}
          </CardMotion>
        ))}
      </div>
      {skip + offset < all && (
        <div
          className="pf-actions pf-more"
          aria-busy={busy || undefined}
          data-re-reveal={lookSystem ? "" : undefined}
        >
          <span className={button.className}>
            <RippleCtaButtonComponent
              id={`more-${request.sectionKey}-${request.tab ?? "list"}`}
              itemIndex={0}
              config={button.config}
              onClick={more}
            />
          </span>
        </div>
      )}
    </>
  );
}
